import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import React, { useEffect, useMemo, useState } from 'react';
import { FormDatePicker } from '@app/components/FormControl/FormDatePicker';
import { FormInputText } from '@app/components/FormControl/FormInputText';
import { FormSelectDropdown } from '@app/components/FormControl/FormSelectDropdown';
import TnkDialog from '@app/components/TnkDialog';
import {
  MAX_DATE,
  MAX_PAGINATOR_ITEMS,
  MIN_DATE,
  PAGINATOR_OPTIONS,
  SCREEN_IDS,
  SORT_ORDER,
} from '@app/consts/common_consts';
import { useForm } from '@app/hooks/useForm';
import { PaginationState, SortState } from '@type/common';
import { TFunction } from 'i18next';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { FormProvider, UseFormClearErrors, UseFormSetError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import './styles.scss';

import { setProjectIdSelection, setVisibleProjectSelection } from '@app/features/projectSelectionSlice';
import { AppDispatch, RootState } from '@app/features/store';
import { createPaginatorTemplate, customSortIcon, ellipsisCellTemplate } from '@app/hooks/useDataTable/dataTableHelper';
import { useLocalStorageSync } from '@app/hooks/useLocalStorageSync';
import { MessageUtils } from '@app/modules/errors';
import { LANGUAGE_DEFAULT } from '@app/modules/i18n-locales/i18n';
import { NAVIGATE_ROUTE } from '@app/modules/routes/common-route';
import { commonApiService } from '@app/services/commonApi';
import {
  FilterProjectSelection,
  MasterCodeItemResponse,
  MasterCodePayload,
  SearchProject,
  SearchProjectPayload,
} from '@app/types/initScreen';
import { formatDateForApi, formatToYMD, toApiIsoEndOfDayUtc } from '@app/utils/DateUtils';
import { getSortOrderDataTable } from '@app/utils/filterDataUtils';
import { getCurrentLang } from '@app/utils/GetCurrentLang';
import { Column } from 'primereact/column';
import { DataTable, DataTableStateEvent } from 'primereact/datatable';
import { Paginator } from 'primereact/paginator';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'primereact/tooltip';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { showErrorToast } from '../CommonToast';
import RowsPerPageSelector from '../RowsPerPageSelector/RowsPerPageSelector';
import TableNoData from '../TableNoData';
import { mapMasterCodeToDropdownOptions } from './selectionProject.util';

dayjs.extend(customParseFormat);

const DATE_FORMAT = 'YYYY/MM/DD';

const DATE_FIELDS = [
  'constructionPeriodFrom',
  'constructionPeriodTo',
  'completionDateFrom',
  'completionDateTo',
] as const;

type DateFieldName = (typeof DATE_FIELDS)[number];

type FilterDateFields = {
  constructionPeriodFrom: unknown;
  constructionPeriodTo: unknown;
  completionDateFrom: unknown;
  completionDateTo: unknown;
};

const isFormatValid = (d: Date): boolean => {
  const str = dayjs(d).format(DATE_FORMAT);
  return dayjs(str, DATE_FORMAT, true).isValid();
};

const isValidDate = (d: unknown): d is Date => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return false;
  return isFormatValid(d) && d >= MIN_DATE && d <= MAX_DATE;
};

const isInvalidDate = (d: unknown): boolean => {
  if (d === null || d === undefined) return false;
  if (!(d instanceof Date)) return true;
  if (Number.isNaN(d.getTime())) return true;
  return !isFormatValid(d) || d < MIN_DATE || d > MAX_DATE;
};

const validateDateFields = (
  values: FilterDateFields,
  setError: UseFormSetError<any>,
  clearErrors: UseFormClearErrors<any>,
  t: TFunction
): boolean => {
  const errInvalid = t('MSG_ERR_COM_00030', { ns: 'messages' });
  const errRange = t('MSG_ERR_COM_00036', { ns: 'messages' });

  clearErrors(DATE_FIELDS);

  let hasError = false;

  const setErr = (field: DateFieldName, msg: string) => {
    setError(field, { message: msg });
    hasError = true;
  };

  if (isInvalidDate(values.constructionPeriodFrom)) setErr('constructionPeriodFrom', errInvalid);
  else if (
    isValidDate(values.constructionPeriodFrom) &&
    isValidDate(values.constructionPeriodTo) &&
    values.constructionPeriodFrom > values.constructionPeriodTo
  )
    setErr('constructionPeriodFrom', errRange);

  if (isInvalidDate(values.constructionPeriodTo)) setErr('constructionPeriodTo', errInvalid);
  else if (
    isValidDate(values.constructionPeriodTo) &&
    isValidDate(values.constructionPeriodFrom) &&
    values.constructionPeriodTo < values.constructionPeriodFrom
  )
    setErr('constructionPeriodTo', errRange);

  if (isInvalidDate(values.completionDateFrom)) setErr('completionDateFrom', errInvalid);
  else if (
    isValidDate(values.completionDateFrom) &&
    isValidDate(values.completionDateTo) &&
    values.completionDateFrom > values.completionDateTo
  )
    setErr('completionDateFrom', errRange);

  if (isInvalidDate(values.completionDateTo)) setErr('completionDateTo', errInvalid);
  else if (
    isValidDate(values.completionDateTo) &&
    isValidDate(values.completionDateFrom) &&
    values.completionDateTo < values.completionDateFrom
  )
    setErr('completionDateTo', errRange);

  return !hasError;
};

type FilterFormValues = {
  nspt20ProjectId: string;
  projectName: string;
  jurisdictionCode: string | null;
  salesCode: string;
  designCode: string;
  estimateCode: string;
  constructionCode: string;
  buildingOwner: string;
  constructionPeriodFrom: Date | null;
  constructionPeriodTo: Date | null;
  completionDateFrom: Date | null;
  completionDateTo: Date | null;
};

const DialogSelectionProject: React.FC = () => {
  const [masterCodeItems, setMasterCodeItems] = useState<Omit<MasterCodeItemResponse, 'content'>>();
  const [pageTotal, setPageTotal] = useState({
    totalRecords: 0,
    totalPages: 1,
  });
  const [pageState, setPageState] = useState<PaginationState>({
    page: 1,
    pageSize: PAGINATOR_OPTIONS[0],
  });

  const [sortState, setSortState] = useState<SortState>({
    sortBy: 'updatedAt',
    sortOrder: SORT_ORDER.DESC,
  });

  const [projects, setProjects] = useState<SearchProject[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { visible, fromScreenRouter } = useSelector((state: RootState) => state.projectSelection);
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const lang = getCurrentLang();
  const [_v, setStorageValue] = useLocalStorageSync<number | null>('projectId', null);
  const navigate = useNavigate();

  const form = useForm<FilterFormValues>({
    mode: 'onSubmit',
    defaultValues: {
      nspt20ProjectId: '',
      projectName: '',
      jurisdictionCode: null,
      constructionCode: '',
      designCode: '',
      estimateCode: '',
      buildingOwner: '',
      constructionPeriodFrom: null,
      constructionPeriodTo: null,
      completionDateFrom: null,
      completionDateTo: null,
      salesCode: '',
    },
  });

  useEffect(() => {
    async function fetchMasterCodes() {
      try {
        const payload: MasterCodePayload = {
          masterTypeCodes: ['BM0011'],
        };
        const response = await commonApiService.getMasterCodeOptions(payload);
        setMasterCodeItems(response.data.content);
      } catch (error: any) {
        showErrorToast(MessageUtils.formatApiMessage(error) || t(error?.code, { ns: 'messages' }));
      }
    }

    if (visible) {
      fetchMasterCodes();
    }
  }, [visible]);

  useEffect(() => {
    if (visible) {
      searchProjects(form.getValues(), sortState as any);
    }
  }, [pageState, sortState, visible]);

  const searchProjects = async (
    searchValues: FilterFormValues,
    sortConditions: { sortBy: string | null; sortOrder: string }
  ) => {
    try {
      setIsLoading(true);

      const searchFields: FilterProjectSelection = {
        ...searchValues,
        constructionPeriodFrom: searchValues.constructionPeriodFrom
          ? formatDateForApi(searchValues.constructionPeriodFrom)!
          : '',
        constructionPeriodTo: toApiIsoEndOfDayUtc(searchValues.constructionPeriodTo),
        completionDateFrom: searchValues.completionDateFrom ? formatDateForApi(searchValues.completionDateFrom)! : '',
        completionDateTo: toApiIsoEndOfDayUtc(searchValues.completionDateTo),
        jurisdictionCode: searchValues.jurisdictionCode || '',
      };

      const payload: SearchProjectPayload = {
        pagination: pageState,
        searchConditions: searchFields,
        sortConditions: {
          ...sortConditions,
          sortBy: sortState.sortBy || null,
        },
      };
      const response = await commonApiService.searchProject(payload);
      setProjects(response.data.content);
      setPageTotal(response.data.pagination);
    } catch (error: any) {
      showErrorToast(MessageUtils.formatApiMessage(error) || t(error?.code, { ns: 'messages' }));
    } finally {
      setIsLoading(false);
    }
  };

  const jurisdictionOptions = useMemo(() => {
    return mapMasterCodeToDropdownOptions(masterCodeItems?.BM0011);
  }, [lang, masterCodeItems]);

  const header = <span className="fw-700 body-sm text-black-100">{t('cm004.dialog.table.lblProjectList')}</span>;

  const handleClear = () => {
    form.reset();
  };

  const handleSearch = form.handleSubmit(async (values: FilterFormValues) => {
    await searchProjects(values, sortState as any);
  });

  const onHide = () => {
    form.reset();
    dispatch(setVisibleProjectSelection({ visible: false }));
  };

  const onPageChange = (event: any) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    setPageState({ page: newPage, pageSize: event.rows });
  };

  const paginatorTemplate = createPaginatorTemplate(MAX_PAGINATOR_ITEMS);

  const jurisdictionBodyTemplate = (rowData: SearchProject) => {
    return ellipsisCellTemplate(lang === LANGUAGE_DEFAULT ? 'jurisdictionNameJa' : 'jurisdictionNameEn')(rowData);
  };

  const dateBodyTemplate = (rowData: SearchProject, field: string) => {
    const dateValue = rowData[field as keyof SearchProject] as string;
    return <span>{dateValue ? formatToYMD(dateValue) : '-'}</span>;
  };

  const handleSelectProject = (projectId: number) => {
    setStorageValue(projectId);
    dispatch(setProjectIdSelection(projectId));
    dispatch(setVisibleProjectSelection({ visible: false }));
    // Reload the page to reflect the selected project
    window.location.reload();

    if ([SCREEN_IDS.SC004, SCREEN_IDS.SC012].includes(fromScreenRouter as SCREEN_IDS)) {
      navigate(`${NAVIGATE_ROUTE.SC004_DETAIL}`);
    }
  };

  const actionBodyTemplate = (rowData: SearchProject) => {
    return (
      <Button
        label={t('cm004.dialog.table.btnSelect')}
        className="btn btn-secondary h-[28]"
        onClick={() => handleSelectProject(rowData.projectId)}
        style={{ padding: '0 22px' }}
        pt={{ label: { className: 'body-ssm' } }}
      />
    );
  };

  const onSort = (e: DataTableStateEvent) => {
    const newSort = {
      sortBy: e.sortField,
      sortOrder: e.sortOrder === -1 ? SORT_ORDER.DESC : SORT_ORDER.ASC,
    };
    setSortState(newSort);
    setPageState((prev) => ({ ...prev, page: 1 }));
  };

  return (
    <TnkDialog
      blockScroll
      onHide={onHide}
      header={t('cm004.dialog.lblDialogTitle')}
      draggable={false}
      visible={visible}
      modal
      position="center"
      className="w-11 h-[732] dialog-selection-project"
    >
      <Tooltip target=".cell-ellipsis" className="custom-tooltip data-table-tooltip" />
      <div className="content-block">
        <Card header={t('cm004.dialog.search.lblSearchCond')} className="card-with-header no-body-padding">
          <FormProvider {...form}>
            <div>
              {/* Row 1 */}
              <div className="flex gap-3">
                <div className="w-3 pb-0">
                  <FormInputText
                    name="nspt20ProjectId"
                    placeholder={t('cm004.dialog.search.txtProjId')}
                    label={t('cm004.dialog.search.lblProjId')}
                    type="text"
                  />
                </div>
                <div className="w-3 pb-0">
                  <FormInputText
                    name="projectName"
                    label={t('cm004.dialog.search.lblProjName')}
                    placeholder={t('cm004.dialog.search.txtProjName')}
                    type="text"
                  />
                </div>
                <div className="w-3 pb-0">
                  <FormSelectDropdown
                    name="jurisdictionCode"
                    label={t('cm004.dialog.search.lblJurisdiction')}
                    options={jurisdictionOptions}
                    defaultOptional={t('cm004.dialog.search.txtJurisdiction')}
                    defaultValue={null}
                    value={form.watch('jurisdictionCode')}
                  />
                </div>
                <div className="w-3 pb-0">
                  <FormInputText
                    name="salesCode"
                    placeholder={t('cm004.dialog.search.txtEstConstCode')}
                    label={t('cm004.dialog.search.lblEstConstCode')}
                    type="text"
                  />
                </div>
              </div>

              {/* Row 2 */}
              <div className="flex gap-3 mt-3">
                <div className="w-3 py-0">
                  <FormInputText
                    name="designCode"
                    placeholder={t('cm004.dialog.search.txtDesignCode')}
                    label={t('cm004.dialog.search.lblDesignCode')}
                    type="text"
                  />
                </div>
                <div className="w-3 py-0">
                  <FormInputText
                    name="estimateCode"
                    placeholder={t('cm004.dialog.search.txtEstimateCode')}
                    label={t('cm004.dialog.search.lblEstimateCode')}
                    type="text"
                  />
                </div>
                <div className="w-3 py-0">
                  <FormInputText
                    name="constructionCode"
                    placeholder={t('cm004.dialog.search.txtConstCode')}
                    label={t('cm004.dialog.search.lblConstCode')}
                    type="text"
                  />
                </div>
                <div className="w-3 py-0">
                  <FormInputText
                    name="buildingOwner"
                    placeholder={t('cm004.dialog.search.txtOwner')}
                    label={t('cm004.dialog.search.lblOwner')}
                    type="text"
                  />
                </div>
              </div>

              {/* Row 3 */}
              <div className="flex gap-3 mt-3">
                <div className="w-6 py-0">
                  <div className="flex align-items-center">
                    <div className="flex-1">
                      <FormDatePicker
                        name="constructionPeriodFrom"
                        label={t('cm004.dialog.search.lblStartDate')}
                        placeholder={t('cm004.dialog.search.txtDatePlaceholder')}
                        keepInvalid
                      />
                    </div>
                    <div className="flex-none mt-3 mx-3 text-color-black text-xs" style={{ width: '12px' }}>
                      ～
                    </div>
                    <div className="flex-1">
                      <FormDatePicker
                        name="constructionPeriodTo"
                        label="&nbsp;"
                        placeholder={t('cm004.dialog.search.txtDatePlaceholder')}
                        keepInvalid
                      />
                    </div>
                  </div>
                </div>
                <div className="w-6 py-0">
                  <div className="flex align-items-center">
                    <div className="flex-1">
                      <FormDatePicker
                        name="completionDateFrom"
                        label={t('cm004.dialog.search.lblEndDate')}
                        placeholder={t('cm004.dialog.search.txtDatePlaceholder')}
                        keepInvalid
                      />
                    </div>
                    <div className="flex-none mt-3 mx-3 text-color-black text-xs" style={{ width: '12px' }}>
                      ～
                    </div>
                    <div className="flex-1">
                      <FormDatePicker
                        name="completionDateTo"
                        label="&nbsp;"
                        placeholder={t('cm004.dialog.search.txtDatePlaceholder')}
                        keepInvalid
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 4 */}
              <div className="grid mt-3 action-row">
                <div className="col-12 flex justify-content-end align-items-end gap-1 pb-0">
                  <Button
                    label={t('cm004.dialog.search.btnClear')}
                    onClick={handleClear}
                    className="btn btn-secondary h-[28]"
                    pt={{ label: { className: 'body-ssm' } }}
                    style={{ padding: '0 15px' }}
                  />
                  <Button
                    label={t('cm004.dialog.search.btnSearch')}
                    onClick={handleSearch}
                    className="btn btn-primary h-[28]"
                    pt={{ label: { className: 'body-ssm' } }}
                    style={{ padding: '0 15px' }}
                  />
                </div>
              </div>
            </div>
          </FormProvider>
        </Card>

        {isLoading ? (
          <div className="flex justify-content-center align-items-center top-panel-spinner mt-5">
            <ProgressSpinner />
          </div>
        ) : (
          <>
            <DataTable
              value={projects}
              showGridlines
              className="p-datatable p-datatable-with-header mt-3"
              emptyMessage={<TableNoData message={t('MSG_INF_COM_00106', { ns: 'messages' })} />}
              scrollHeight="190px"
              header={header}
              sortField={sortState.sortBy}
              sortOrder={getSortOrderDataTable(sortState.sortOrder)}
              onSort={onSort}
              sortIcon={customSortIcon}
              scrollable
            >
              <Column
                field="nspt20ProjectId"
                header={t('cm004.dialog.table.colProjId')}
                sortable
                style={{ width: '8%' }}
              />
              <Column
                field="projectName"
                header={t('cm004.dialog.table.colProjName')}
                sortable
                style={{ width: '16%' }}
                body={ellipsisCellTemplate('projectName')}
              />
              <Column
                field="jurisdictionCode"
                sortable
                header={t('cm004.dialog.table.colJurisdiction')}
                style={{ width: '10%' }}
                body={jurisdictionBodyTemplate}
              />
              <Column
                field="salesCode"
                sortable
                header={t('cm004.dialog.table.colSalesCode')}
                style={{ width: '8%' }}
                body={ellipsisCellTemplate('salesCode')}
              />
              <Column
                field="designCode"
                sortable
                header={t('cm004.dialog.table.colDesignCode')}
                style={{ width: '8%' }}
                body={ellipsisCellTemplate('designCode')}
              />
              <Column
                field="estimateCode"
                sortable
                header={t('cm004.dialog.table.colEstimateCode')}
                style={{ width: '8%' }}
                body={ellipsisCellTemplate('estimateCode')}
              />
              <Column
                field="constructionCode"
                sortable
                header={t('cm004.dialog.table.colConstCode')}
                style={{ width: '8%' }}
                body={ellipsisCellTemplate('constructionCode')}
              />
              <Column
                field="buildingOwner"
                sortable
                header={t('cm004.dialog.table.colOwner')}
                style={{ width: '10%' }}
                body={ellipsisCellTemplate('buildingOwner')}
              />
              <Column
                field="constructionStartDate"
                sortable
                header={t('cm004.dialog.table.colStartDate')}
                style={{ width: '8%' }}
                body={(rowData) => dateBodyTemplate(rowData, 'constructionStartDate')}
              />
              <Column
                field="completionDate"
                sortable
                header={t('cm004.dialog.table.colEndDate')}
                style={{ width: '8%' }}
                body={(rowData) => dateBodyTemplate(rowData, 'completionDate')}
              />
              <Column
                field="action"
                header={t('cm004.dialog.table.colAction')}
                style={{ width: '8%' }}
                body={actionBodyTemplate}
              />
            </DataTable>

            {projects.length > 0 && (
              <div className="py-3">
                <Paginator
                  first={(pageState.page - 1) * pageState.pageSize}
                  rows={pageState.pageSize}
                  totalRecords={pageTotal.totalRecords}
                  rowsPerPageOptions={PAGINATOR_OPTIONS}
                  onPageChange={onPageChange}
                  template={paginatorTemplate}
                  pageLinkSize={MAX_PAGINATOR_ITEMS}
                  leftContent={
                    <RowsPerPageSelector
                      rows={pageState.pageSize}
                      onRowsChange={(newRows) => {
                        setPageState({ page: 1, pageSize: newRows });
                      }}
                      options={PAGINATOR_OPTIONS}
                    />
                  }
                />
              </div>
            )}
          </>
        )}
      </div>
    </TnkDialog>
  );
};

export default DialogSelectionProject;
